// src/sections/PrivacyPolicy.jsx
import React from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-2xl font-bold text-darkblue1 mb-3">{title}</h2>
    <div className="space-y-3 text-darkblue2 text-lg leading-relaxed">
      {children}
    </div>
  </div>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/');
  };

  return (
    <section className="bg-white text-darkblue1 font-cormorant py-24 px-6 md:px-10">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={handleGoHome}
          className="inline-flex items-center gap-2 text-darkred1 hover:text-darkred2 font-semibold mb-10 transition"
        >
          <FaArrowLeft /> Voltar para o início
        </button>

        <h1 className="text-4xl md:text-5xl font-bold text-darkblue1 mb-4">
          Política de Privacidade
        </h1>
        <p className="text-darkblue2 mb-8">
          Última atualização: agosto de 2026
        </p>

        <p className="text-darkblue2 text-lg leading-relaxed mb-10">
          A presente Política de Privacidade tem como objetivo informar como os dados pessoais
          fornecidos por usuários e interessados nos serviços da Psicóloga Erivan Antônia Figueredo
          são coletados, utilizados e protegidos.
        </p>
        <p className="text-darkblue2 text-lg leading-relaxed mb-10">
          O tratamento dos dados pessoais é realizado de acordo com a Lei nº 13.709/2018 – Lei Geral
          de Proteção de Dados Pessoais (LGPD).
        </p>

        <Section title="1. Dados que podem ser coletados">
          <p>
            Ao entrar em contato por meio deste site, formulários, anúncios, redes sociais ou outros
            canais de comunicação disponibilizados, poderão ser coletados dados como:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>nome;</li>
            <li>telefone;</li>
            <li>endereço de e-mail;</li>
            <li>empresa e função profissional, quando aplicável;</li>
            <li>informações fornecidas voluntariamente em mensagens ou formulários de contato;</li>
            <li>respostas fornecidas em formulários de anúncios ou campanhas.</li>
          </ul>
        </Section>

        <Section title="2. Para que os dados são utilizados">
          <p>Os dados fornecidos poderão ser utilizados para:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>responder solicitações de contato;</li>
            <li>fornecer informações sobre serviços profissionais;</li>
            <li>compreender a necessidade apresentada pelo interessado;</li>
            <li>
              realizar contato relacionado a palestras, treinamentos, saúde mental no trabalho,
              riscos psicossociais e demais serviços oferecidos;
            </li>
            <li>realizar agendamentos e comunicações solicitadas pelo próprio interessado.</li>
          </ul>
          <p>
            Os dados não serão utilizados para finalidades incompatíveis com aquelas que motivaram
            sua coleta.
          </p>
        </Section>

        <Section title="3. Compartilhamento de dados">
          <p>Os dados pessoais não serão vendidos ou comercializados.</p>
          <p>
            Poderão ser utilizados serviços de terceiros necessários ao funcionamento dos canais de
            comunicação e divulgação, como plataformas de hospedagem, formulários, redes sociais,
            WhatsApp e ferramentas da Meta, sujeitos também às respectivas políticas de privacidade.
          </p>
          <p>
            Quando necessário, o compartilhamento de informações ocorrerá apenas dentro dos limites
            necessários para a prestação do serviço, cumprimento de obrigação legal ou exercício
            regular de direitos.
          </p>
        </Section>

        <Section title="4. Proteção e armazenamento">
          <p>
            São adotadas medidas razoáveis de segurança e privacidade para proteger os dados pessoais
            contra acessos não autorizados, perda, alteração, divulgação ou utilização indevida.
          </p>
          <p>
            Os dados serão mantidos pelo período necessário para atender às finalidades para as
            quais foram coletados ou para cumprimento de obrigações legais e regulatórias aplicáveis.
          </p>
        </Section>

        <Section title="5. Direitos do titular dos dados">
          <p>Nos termos da LGPD, o titular poderá solicitar, quando aplicável:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>confirmação da existência de tratamento de seus dados;</li>
            <li>acesso aos seus dados pessoais;</li>
            <li>correção de dados incompletos ou desatualizados;</li>
            <li>exclusão ou anonimização, quando cabível;</li>
            <li>informações sobre o tratamento de seus dados;</li>
            <li>revogação do consentimento, quando esta for a base utilizada para o tratamento.</li>
          </ul>
        </Section>

        <Section title="6. Dados relacionados a atendimento psicológico">
          <p>
            Informações eventualmente relacionadas à prestação de serviços psicológicos recebem
            tratamento compatível com sua natureza e com as normas éticas e profissionais aplicáveis
            à Psicologia.
          </p>
          <p>
            Esta Política de Privacidade não altera ou reduz o dever de sigilo profissional aplicável
            aos atendimentos psicológicos.
          </p>
        </Section>

        <Section title="7. Links e serviços de terceiros">
          <p>
            Este site poderá disponibilizar links ou formas de contato por plataformas externas,
            como Instagram, WhatsApp e outros serviços. O tratamento realizado diretamente por essas
            plataformas também está sujeito às políticas e condições estabelecidas por seus
            respectivos responsáveis.
          </p>
        </Section>

        <Section title="8. Alterações desta política">
          <p>
            Esta Política de Privacidade poderá ser atualizada sempre que necessário para refletir
            mudanças nos serviços, ferramentas utilizadas ou requisitos legais.
          </p>
        </Section>

        <Section title="9. Contato">
          <p>
            Em caso de dúvidas sobre esta Política de Privacidade ou para exercer direitos
            relacionados aos seus dados pessoais, entre em contato:
          </p>
          <p>
            Erivan Antônia Figueredo – Psicóloga
            <br />
            CRP 06/78775
            <br />
            E-mail:{' '}
            <a href="mailto:contato@psicologaerivan.com.br" className="text-darkred1 hover:underline">
              contato@psicologaerivan.com.br
            </a>
          </p>
        </Section>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
